'use client'

import { getPlatformName } from '@/actions/platform'
import LoginButton from '@/components/_registration/login-button'
import CarIcon from '@/components/icons/car-icon'
import ControllerIcon from '@/components/icons/controller-icon'
import MedalIcon from '@/components/icons/medal'
import NavigationIcon from '@/components/icons/navigation'
import RouteIcon from '@/components/icons/route-icon'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { localDateTime, timezoneDateTime } from '@/helpers/datetime'
import useNowTicker from '@/hooks/use-now-ticker'
import dayjs from '@/lib/dayjs'
import { resolveEventImageUrl } from '@/lib/racing-images'
import { cn } from '@/lib/utils'
import { CalendarIcon, Clock3, Flag, Users } from 'lucide-react'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import Link from 'next/link'
import React, { useEffect, useRef, useState, useSyncExternalStore } from 'react'

const subscribeToHydration = () => () => {}

const EventAttendeeStatusButton = dynamic(
  () =>
    import('@/app/(public)/c/[communitySlug]/[championshipSlug]/[eventId]/components/event-attendee-status-button'),
  {
    loading: () => <Skeleton className="h-full w-full rounded-sm" />,
  },
)

function EventImageContent({ event, disablePrefetch }) {
  const isSingleEvent = event.championship?.isSingleEvent
  const eventRoundNumber = event.eventRoundNumber

  return (
    <Link
      href={`/c/${event.championship.community.slug}/${event.championship.slug}`}
      prefetch={disablePrefetch ? false : undefined}
      className={cn('relative grid aspect-video')}
    >
      <Image
        fill
        className="rounded-sm object-cover"
        src={resolveEventImageUrl(event)}
        alt="Event cover image"
        sizes="(max-width: 767px) 100vw, (max-width: 1279px) 50vw, 33vw"
      />
      <div className="absolute bottom-5 left-7 right-7 flex flex-wrap items-center gap-1">
        {isSingleEvent ? (
          <div className="flex items-center gap-1 rounded-full bg-[#FFEEFEEE] px-3 py-1">
            <NavigationIcon className="size-4 stroke-[#DA0EBC]" />
            <p className="text-cap font-bold text-[#DA0EBC]">Single Event</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-1 rounded-full bg-[#EEF2FF] px-3 py-1">
              <MedalIcon className="size-4 stroke-[#484BD0] stroke-2" />
              <p className="text-cap font-bold text-[#484BD0]">Championship</p>
            </div>
            <div className="flex items-center gap-1 rounded-full bg-[#EEF2FF] px-3 py-1">
              <p className="text-cap font-bold text-[#484BD0]">
                Event {eventRoundNumber} / {event.championship.totalEventCount}
              </p>
            </div>
          </>
        )}
      </div>
    </Link>
  )
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className="relative z-10 flex-1"
    >
      <div className="relative py-2">
        <div
          className={cn(
            'absolute inset-0 flex items-center justify-center transition-colors duration-300',
            active ? 'text-neutral-blue-900' : 'text-white',
          )}
        >
          {children}
        </div>
      </div>
    </button>
  )
}

function TabSwitcher({ activeTab, onTabChange }) {
  return (
    <div className="relative flex h-[52px] justify-between overflow-hidden rounded-md border border-neutral-blue-600 p-1">
      <div
        className={cn(
          'absolute top-1 h-[calc(100%-8px)] transition-all duration-300 ease-in-out',
          {
            'left-1 w-[calc(50%-4px)]': activeTab === 'details',
            'left-1/2 w-[calc(50%-4px)]': activeTab === 'format',
          },
        )}
      >
        <div className="h-full w-full rounded-sm bg-white" />
      </div>
      <div className="absolute inset-0 flex w-full">
        <TabButton
          active={activeTab === 'details'}
          onClick={() => onTabChange('details')}
        >
          Details
        </TabButton>
        <TabButton
          active={activeTab === 'format'}
          onClick={() => onTabChange('format')}
        >
          Format
        </TabButton>
      </div>
    </div>
  )
}

function EventContent({
  event,
  session,
  initialAttendance,
  disablePrefetch,
  viewOnly,
  showBottomStatus,
  showRegistrationCountdownOnly,
}) {
  const [activeTab, setActiveTab] = useState('details')
  const [driverCount, setDriverCount] = useState(event._count?.attendees)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  const nowDate = mounted ? new Date() : null

  // Determine if event is live or has finished
  const startDate = event.startDateTime ? new Date(event.startDateTime) : null
  const finishDate = event.finishDateTime
    ? new Date(event.finishDateTime)
    : null
  const eventIsLive = Boolean(
    mounted &&
    startDate &&
    startDate <= nowDate &&
    (!finishDate || finishDate > nowDate),
  )
  const eventHasFinished = Boolean(
    mounted && finishDate && finishDate < nowDate,
  )

  return (
    <div
      className={cn(
        'flex h-full flex-col gap-2 px-4 pt-2 tablet:px-8 tablet:pt-4',
        showBottomStatus ? 'pb-2 tablet:pb-4' : 'pb-4 tablet:pb-8',
      )}
    >
      <Link
        href={`/c/${event.championship.community.slug}/${event.championship.slug}/${event.id}`}
        prefetch={disablePrefetch ? false : undefined}
        className="truncate-1 text-h-5-sm font-semibold desktop-xs:text-h-5"
      >
        {event.name}
      </Link>

      <Link
        href={`/c/${event.championship.community.slug}`}
        prefetch={disablePrefetch ? false : undefined}
        className="flex items-center gap-2"
      >
        <div className="relative grid aspect-square size-6 overflow-hidden rounded-sm desktop-xs:size-9">
          <Image
            fill
            className="object-fill"
            src={
              event.championship.community.logoImgUrl ||
              '/img/default/profile-pic.webp'
            }
            alt="Community logo"
            sizes="(max-width: 1279px) 24px, 36px"
          />
        </div>
        <p className="truncate-1 text-p-1 font-bold text-neutral-blue-200">
          {event.championship.community.name}
        </p>
      </Link>

      <div className="mt-1 flex w-fit items-center gap-1 rounded-full bg-warning-50 px-2 py-1">
        <ControllerIcon className="size-4 stroke-warning-700 stroke-2" />
        <p className="text-cap font-bold text-warning-700">
          {event.championship.game.nicename} -{' '}
          {getPlatformName(event.championship.platforms)}
        </p>
      </div>

      <TabSwitcher activeTab={activeTab} onTabChange={setActiveTab} />

      {activeTab === 'details' ? (
        <EventDetails
          event={event}
          driverCount={driverCount}
          mounted={mounted}
        />
      ) : (
        <EventFormat event={event} />
      )}

      <div className="flex flex-1 flex-col justify-end">
        {/* Buttons - shared between both tabs */}
        <div
          className={cn(
            'flex flex-col text-p-2 font-semibold tablet:h-[54px] tablet:flex-row',
            eventHasFinished || viewOnly
              ? 'justify-center'
              : 'justify-between gap-4',
          )}
        >
          <Link
            href={`/c/${event.championship.community.slug}/${event.championship.slug || event.championship.id}/${event.id}`}
            prefetch={disablePrefetch ? false : undefined}
            className={cn(
              'group grid min-h-[48px] items-center rounded-sm border border-neutral-blue-600 bg-neutral-blue-700 text-center transition-all duration-200 hover:border-neutral-blue-500 hover:bg-neutral-blue-600 active:bg-neutral-blue-700',
              eventIsLive &&
                'border-success-500 bg-success-500/10 hover:border-success-500 hover:bg-success-500/15',
              eventHasFinished || viewOnly ? 'w-full' : 'flex-1',
            )}
          >
            <span
              className={cn(
                'relative after:absolute after:bottom-0 after:left-1/2 after:h-px after:w-0 after:-translate-x-1/2 after:bg-white after:transition-all after:duration-200 group-hover:after:w-4/5',
                eventIsLive && 'text-success-500 after:bg-success-500',
              )}
            >
              {eventIsLive ? 'View Live Event' : 'View Event'}
            </span>
          </Link>
          {!eventHasFinished && !viewOnly && (
            <div className="min-h-[48px] flex-1">
              {session ? (
                <EventAttendeeStatusButton
                  event={event}
                  session={session}
                  initialAttendance={initialAttendance}
                  increaseDriverCount={() => setDriverCount(driverCount + 1)}
                  decreaseDriverCount={() => setDriverCount(driverCount - 1)}
                  showRegistrationCountdownOnly={showRegistrationCountdownOnly}
                />
              ) : (
                <LoginButton />
              )}
            </div>
          )}
        </div>
        {showBottomStatus && (
          <EventBottomStatus event={event} mounted={mounted} />
        )}
      </div>
    </div>
  )
}

function EventBottomStatus({ event, mounted }) {
  const now = useNowTicker()

  // Keep first client render identical to server render to avoid hydration mismatch.
  if (!mounted) {
    return <div className="mt-3 h-5 desktop-xs:mt-4" />
  }

  const nowDate = new Date(now)
  const startDate = event?.startDateTime ? new Date(event.startDateTime) : null
  const finishDate = event?.finishDateTime
    ? new Date(event.finishDateTime)
    : null

  if (!startDate || Number.isNaN(startDate.getTime())) {
    return <div className="mt-3 h-5 desktop-xs:mt-4" />
  }

  const isLive = startDate <= nowDate && (!finishDate || finishDate > nowDate)
  const isFinished = finishDate && finishDate <= nowDate

  if (isLive) {
    return (
      <div className="relative mx-auto mt-3 h-5 text-center font-bold desktop-xs:mt-4">
        <p className="absolute font-syncopate uppercase text-success">LIVE</p>
        <p className="animate-[ping_2s_cubic-bezier(0,0,0.2,2)_infinite] font-syncopate uppercase text-success">
          LIVE
        </p>
      </div>
    )
  }

  if (isFinished) {
    return (
      <p className="mt-3 h-5 self-center text-p-1 text-neutral-blue-400 desktop-xs:mt-4">
        Event finished
      </p>
    )
  }

  const nowDayjs = dayjs(nowDate)
  const startDayjs = dayjs(startDate)
  const diff = startDayjs.diff(nowDayjs)
  const duration = dayjs.duration(diff)

  let countdown = startDayjs.from(nowDayjs, true)
  if (duration.asMinutes() < 60) {
    countdown = `${Math.max(duration.minutes(), 0)}m ${Math.max(duration.seconds(), 0)}s`
  } else if (duration.asHours() < 24) {
    countdown = `${Math.max(Math.floor(duration.asHours()), 0)}h ${Math.max(duration.minutes(), 0)}m`
  }

  return (
    <p className="mt-3 h-5 self-center text-p-1 text-neutral-blue-300 desktop-xs:mt-4">
      Race starts in {countdown}
    </p>
  )
}

function EventDetails({ event, driverCount, mounted }) {
  const isHydrated = useSyncExternalStore(
    subscribeToHydration,
    () => true,
    () => false,
  )
  const now = mounted ? new Date() : null
  const startDate = new Date(event.startDateTime)
  const finishDate = event.finishDateTime
    ? new Date(event.finishDateTime)
    : null

  // Determine event status
  const eventHasFinished = Boolean(mounted && finishDate && finishDate < now)
  const eventHasStarted = Boolean(mounted && startDate < now)

  const eventDateTime = eventHasFinished
    ? event.finishDateTime
    : event.startDateTime
  // Calculate cars display data
  const getCarsData = () => {
    if (event.championship?.cars?.length > 0) {
      const firstCar = event.championship.cars[0]?.car?.name
      const remainingCount = event.championship.cars.length - 1
      return {
        name: firstCar,
        remainingCount: remainingCount > 0 ? remainingCount : null,
      }
    } else if (event.championship?.carClasses?.length > 0) {
      const firstCarClass = event.championship.carClasses[0]?.carClass?.name
      const remainingCount = event.championship.carClasses.length - 1
      return {
        name: firstCarClass,
        remainingCount: remainingCount > 0 ? remainingCount : null,
      }
    }
    return { name: 'N/A', remainingCount: null }
  }

  const carsData = getCarsData()

  // Calculate drivers count
  const confirmedDrivers = driverCount || 0
  const driverCountText = !isHydrated
    ? confirmedDrivers
    : event.championship.isGridUnlimited
      ? `${confirmedDrivers}/∞`
      : event.championship.gridPositions > 0
        ? `${confirmedDrivers}/${event.championship.gridPositions}`
        : confirmedDrivers

  // Determine status text
  let statusText = 'Starts'
  if (eventHasFinished) {
    statusText = 'Finished'
  } else if (eventHasStarted) {
    statusText = 'Started'
  }

  return (
    <div className="mt-2 flex flex-col gap-2">
      {/* Event Start/Finish Date */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="grid size-9 items-center justify-center rounded-sm bg-neutral-blue-700">
            <CalendarIcon className="size-5 stroke-neutral-blue-200 stroke-2" />
          </div>
          <p
            className={cn('text-p-2 font-semibold', {
              'text-error-500': eventHasFinished,
            })}
          >
            {mounted ? statusText : 'Event time'}
          </p>
        </div>
        {mounted ? (
          <TimezoneDateTime
            datetime={eventDateTime}
            timezone={event.targetTimezone}
          />
        ) : (
          <Skeleton className="h-5 w-36 rounded-sm" />
        )}
      </div>

      <div className="divide bg-neutral-blue-700!" />

      {/* Track */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <div className="grid size-9 items-center justify-center rounded-sm bg-neutral-blue-700">
            <RouteIcon className="size-5 stroke-neutral-blue-200 stroke-2" />
          </div>
          <p className="text-p-2 font-semibold">Track</p>
        </div>
        <TruncatedText
          text={event.track?.name || event.trackMeta?.name || 'No track set'}
          className="text-p-2"
        />
      </div>

      <div className="divide bg-neutral-blue-700!" />

      {/* Cars */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <div className="grid size-9 items-center justify-center rounded-sm bg-neutral-blue-700">
            <CarIcon className="size-5 stroke-neutral-blue-200 stroke-2" />
          </div>
          <p className="text-p-2 font-semibold">Cars</p>
        </div>
        <CarsDisplay carsData={carsData} championship={event.championship} />
      </div>

      <div className="divide bg-neutral-blue-700!" />

      {/* Drivers */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="grid size-9 items-center justify-center rounded-sm bg-neutral-blue-700">
            <Users className="size-5 stroke-neutral-blue-200 stroke-2" />
          </div>
          <p className="text-p-2 font-semibold">Drivers</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="size-2 rounded-full bg-success-500" />
          <p className="text-p-2">{driverCountText}</p>
        </div>
      </div>

      <div className="divide bg-neutral-blue-700!" />
    </div>
  )
}

function CarsDisplay({ carsData, championship }) {
  // If there's no remaining count, just show the single car/class name with truncation
  if (!carsData.remainingCount) {
    return <TruncatedText text={carsData.name} className="text-p-2" />
  }

  // Get remaining car/car class names for the tooltip (excluding the first one already shown)
  const getRemainingCarNames = () => {
    if (championship?.cars?.length > 0) {
      return championship.cars
        .slice(1)
        .map((car) => car.car?.name)
        .filter(Boolean)
    } else if (championship?.carClasses?.length > 0) {
      return championship.carClasses
        .slice(1)
        .map((carClass) => carClass.carClass?.name)
        .filter(Boolean)
    }
    return []
  }

  const remainingCarNames = getRemainingCarNames()

  // If there are multiple cars, show them in a flex container with proper constraints
  return (
    <div className="flex min-w-0 items-center gap-1">
      <TruncatedText text={carsData.name} className="min-w-0 text-p-2" />
      <TooltipProvider>
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="cursor-pointer whitespace-nowrap text-p-2 text-neutral-blue-300 underline underline-offset-2"
            >
              +{carsData.remainingCount} more
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <div className="max-h-[300px] overflow-y-auto">
              {remainingCarNames.map((carName, index) => (
                <div
                  key={`car-${index}`}
                  className="rounded-sm px-2 py-2 text-p-1 font-semibold text-shades-0 transition-colors group-hover:bg-neutral-blue-700"
                >
                  {carName}
                </div>
              ))}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  )
}

function getSessionIcon(sessionType) {
  switch (sessionType?.toLowerCase()) {
    case 'practice':
      return 'P'
    case 'qualifying':
      return 'Q'
    case 'race':
      return 'R'
    case 'hotlap':
      return 'H'
    default:
      return 'S'
  }
}

function formatSessionName(sessionType) {
  if (!sessionType) return 'Session'
  return `${sessionType.charAt(0).toUpperCase()}${sessionType.slice(1)}`
}

function formatSessionDuration(session) {
  if (!session.duration || !session.durationType) return ''

  const durationValue = Number.parseInt(session.duration, 10)
  if (!Number.isFinite(durationValue)) return ''

  switch (session.durationType.toLowerCase()) {
    case 'min': {
      const hours = Math.floor(durationValue / 60)
      const minutes = durationValue % 60
      const hourText = hours ? `${hours} hour${hours === 1 ? '' : 's'}` : ''
      const minuteText = minutes
        ? `${minutes} min${minutes === 1 ? '' : 's'}`
        : ''
      return [hourText, minuteText].filter(Boolean).join(' ')
    }
    case 'hour':
      return `${durationValue} hour${durationValue === 1 ? '' : 's'}`
    case 'day':
      return `${durationValue} day${durationValue === 1 ? '' : 's'}`
    case 'lap':
      return `${durationValue} lap${durationValue === 1 ? '' : 's'}`
    case 'percent':
      return `${durationValue}%`
    default:
      return `${durationValue} ${session.durationType}`
  }
}

function getSessionFinishDateTime(session, startDateTime) {
  if (!startDateTime) return null

  const startTime = new Date(startDateTime).getTime()
  const durationValue = Number(session.duration)

  if (!Number.isFinite(startTime) || !Number.isFinite(durationValue)) {
    return null
  }

  const durationMultipliers = {
    min: 60 * 1000,
    hour: 60 * 60 * 1000,
    day: 24 * 60 * 60 * 1000,
  }
  const multiplier = durationMultipliers[session.durationType?.toLowerCase()]

  if (!multiplier) return null

  return new Date(startTime + durationValue * multiplier).toISOString()
}

function EventFormatRow({ icon, label, children }) {
  return (
    <>
      <div className="flex min-h-9 items-center justify-between gap-2">
        <div className="flex shrink-0 items-center gap-2">
          <div className="grid size-9 items-center justify-center rounded-sm bg-neutral-blue-700">
            {icon}
          </div>
          <p className="text-p-2 font-semibold">{label}</p>
        </div>
        <div className="min-w-0 text-right text-p-2">{children}</div>
      </div>
      <div className="divide bg-neutral-blue-700!" />
    </>
  )
}

function TimezoneDateTime({
  datetime,
  timezone,
  displayFormat = 'ddd D MMM • HH:mm',
  className,
}) {
  const configuredTimezone = timezone || 'UTC'
  const localTime = localDateTime({
    datetime,
    timezone: configuredTimezone,
    format: displayFormat,
  })
  const configuredTime = timezoneDateTime({
    datetime,
    timezone: configuredTimezone,
    format: 'ddd D MMM • HH:mm z',
  })

  return (
    <TooltipProvider>
      <Tooltip delayDuration={150}>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={`${localTime} local time. Set by organiser as ${configuredTime} in ${configuredTimezone}`}
            className={cn(
              'min-w-0 cursor-help whitespace-nowrap text-right text-p-2',
              className,
            )}
          >
            {localTime}
          </button>
        </TooltipTrigger>
        <TooltipContent>
          <div className="space-y-1 text-left">
            <p className="text-cap font-semibold text-neutral-blue-300">
              Set by organiser
            </p>
            <p className="text-p-1 font-semibold text-shades-0">
              {configuredTime}
            </p>
            <p className="text-cap text-neutral-blue-300">
              {configuredTimezone}
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function SingleSessionFormat({ event, session }) {
  const timezone = session.targetTimezone || event.targetTimezone
  const duration = formatSessionDuration(session)
  const startDateTime = session.targetDateTime || event.startDateTime
  const finishDateTime = getSessionFinishDateTime(session, startDateTime)

  return (
    <div className="mt-2 flex flex-col gap-2">
      <EventFormatRow
        icon={
          <span className="text-p-2 font-bold text-neutral-blue-200">
            {getSessionIcon(session.type)}
          </span>
        }
        label="Session"
      >
        <p>{formatSessionName(session.type)}</p>
      </EventFormatRow>

      <EventFormatRow
        icon={
          <CalendarIcon className="size-5 stroke-neutral-blue-200 stroke-2" />
        }
        label="Starts"
      >
        <TimezoneDateTime datetime={startDateTime} timezone={timezone} />
      </EventFormatRow>

      {duration && (
        <EventFormatRow
          icon={<Clock3 className="size-5 stroke-neutral-blue-200 stroke-2" />}
          label="Duration"
        >
          <p>{duration}</p>
        </EventFormatRow>
      )}

      {finishDateTime && (
        <EventFormatRow
          icon={<Flag className="size-5 stroke-neutral-blue-200 stroke-2" />}
          label="Ends"
        >
          <TimezoneDateTime datetime={finishDateTime} timezone={timezone} />
        </EventFormatRow>
      )}
    </div>
  )
}

function EventFormat({ event }) {
  const sessions = event.sessions || []

  if (sessions.length === 1) {
    return <SingleSessionFormat event={event} session={sessions[0]} />
  }

  const formattedDate = localDateTime({
    datetime: event.startDateTime,
    timezone: event.targetTimezone,
    format: 'ddd D MMM',
  })

  // Show up to 3 sessions, then indicate more available
  const sessionsToShow = sessions.slice(0, 3)
  const hasMoreSessions = sessions.length > 3

  return (
    <div className="mt-2 flex flex-col gap-2">
      {/* Event Date */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="grid size-9 items-center justify-center rounded-sm bg-neutral-blue-700">
            <CalendarIcon className="size-5 stroke-neutral-blue-200 stroke-2" />
          </div>
          <p className="text-p-2 font-semibold">Date</p>
        </div>
        <p className="truncate text-p-2">{formattedDate}</p>
      </div>

      <div className="divide bg-neutral-blue-700!" />

      {/* Sessions */}
      {sessionsToShow.map((session) => {
        const duration = formatSessionDuration(session)
        const sessionIcon = getSessionIcon(session.type)

        return (
          <div key={session.id}>
            <div className="flex h-9 items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <div className="grid size-9 items-center justify-center rounded-sm bg-neutral-blue-700">
                  <span className="text-p-2 font-bold text-neutral-blue-200">
                    {sessionIcon}
                  </span>
                </div>
                <p className="text-p-2 font-semibold">
                  {formatSessionName(session.type)}
                </p>
              </div>
              <div className="flex min-w-0 flex-col items-end justify-center gap-0 text-right">
                <TimezoneDateTime
                  datetime={session.targetDateTime || event.startDateTime}
                  timezone={session.targetTimezone || event.targetTimezone}
                  displayFormat="HH:mm"
                  className="leading-4"
                />
                {duration && (
                  <p className="whitespace-nowrap text-p-1 leading-[14px] text-neutral-blue-300">
                    {duration}
                  </p>
                )}
              </div>
            </div>
            <div className="divide bg-neutral-blue-700! mt-2" />
          </div>
        )
      })}

      {/* More sessions indicator */}
      {hasMoreSessions && (
        <>
          <div className="flex items-center justify-center py-2">
            <p className="text-p-2 text-neutral-blue-400">
              View event to see all sessions
            </p>
          </div>
          <div className="divide bg-neutral-blue-700!" />
        </>
      )}
    </div>
  )
}

// Helper component for truncated text with tooltip
function TruncatedText({ text, className }) {
  const [isOverflowing, setIsOverflowing] = useState(false)
  const textRef = useRef(null)

  useEffect(() => {
    const element = textRef.current
    if (element) {
      setIsOverflowing(element.scrollWidth > element.clientWidth)
    }
  }, [text])

  if (isOverflowing) {
    return (
      <TooltipProvider>
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <button
              type="button"
              ref={textRef}
              aria-label={`Show full value: ${text}`}
              className={cn(
                'min-w-0 cursor-pointer truncate text-right',
                className,
              )}
            >
              {text}
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{text}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  return (
    <p ref={textRef} className={cn('truncate-1 text-right', className)}>
      {text}
    </p>
  )
}

function EventCard({
  event,
  championship,
  session,
  initialAttendance,
  disablePrefetch = false,
  viewOnly = false,
  showBottomStatus = false,
  showRegistrationCountdownOnly = false,
}) {
  if (championship) event.championship = championship

  return (
    <div className="flex h-full flex-col rounded-sm border border-neutral-blue-700 bg-neutral-blue-800">
      <EventImageContent event={event} disablePrefetch={disablePrefetch} />
      <EventContent
        event={event}
        session={session}
        initialAttendance={initialAttendance}
        disablePrefetch={disablePrefetch}
        viewOnly={viewOnly}
        showBottomStatus={showBottomStatus}
        showRegistrationCountdownOnly={showRegistrationCountdownOnly}
      />
    </div>
  )
}

const areEqual = (prevProps, nextProps) => {
  // Only re-render if event, championship, or session changes
  return (
    prevProps.event?.id === nextProps.event?.id &&
    prevProps.championship?.id === nextProps.championship?.id &&
    prevProps.session?.user?.id === nextProps.session?.user?.id &&
    prevProps.initialAttendance?.championshipAttendee?.status ===
      nextProps.initialAttendance?.championshipAttendee?.status &&
    prevProps.initialAttendance?.championshipAttendee?.role ===
      nextProps.initialAttendance?.championshipAttendee?.role &&
    prevProps.initialAttendance?.eventAttendee?.status ===
      nextProps.initialAttendance?.eventAttendee?.status &&
    prevProps.initialAttendance?.eventAttendee?.role ===
      nextProps.initialAttendance?.eventAttendee?.role &&
    prevProps.initialAttendance?.userIsWhitelisted ===
      nextProps.initialAttendance?.userIsWhitelisted &&
    prevProps.disablePrefetch === nextProps.disablePrefetch &&
    prevProps.viewOnly === nextProps.viewOnly &&
    prevProps.showBottomStatus === nextProps.showBottomStatus &&
    prevProps.showRegistrationCountdownOnly ===
      nextProps.showRegistrationCountdownOnly
  )
}

export default React.memo(EventCard, areEqual)
